import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import './BulletPointsWidget.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { shouldInvertIcons, isDark } from '../utils/widgetUtils';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

// appendPx wird jetzt aus LayoutUtils importiert

function BulletPointsWidget({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    console.log('🔍 BulletPointsWidget rendered:', { 
        id: data.id, 
        type: data.type, 
        isSelected, 
        isEditMode,
        bulletPointsCount: data.bulletPoints?.length || 0
    });
    
    const deleteLogo = '/img/delete.svg';
    const defaultBackgroundImage = '/img/crowd.jpg';
    const fonts = useFont();
    
    // States
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const widgetRef = useRef(null);
    const containerRef = useRef(null);

    // Mobile Layout Detection (750px Breakpoint)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // ✅ SMART AUTO-INVERT: Automatische Icon-Invertierung basierend auf Container-Hintergrund
    function shouldAutoInvertIcons() {
        // Wenn manueller Override gesetzt ist, verwende diesen
        if (data.invertIconColors !== undefined && data.invertIconColors !== null) {
            return data.invertIconColors;
        }
        
        // Automatische Erkennung basierend auf Container-Hintergrundfarbe
        const backgroundColor = data.backgroundColor || '#ffffff';
        return isDark(backgroundColor); // Bei dunklem Hintergrund → Icons invertieren (weiß machen)
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    // 🚀 RESPONSIVE LAYOUT STYLES - Mit Revolutionary Layout System
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    });

    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Properties (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 400)
        }),
        // Widget-spezifische Padding aus data.padding (falls gesetzt) oder Standard-Responsive-Padding
        ...(data.padding && { padding: appendPx(data.padding) })
    };

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // Widget Click Handler
    const handleWidgetClick = (event) => {
        console.log('🔍 BulletPointsWidget clicked:', { 
            isEditMode, 
            hasOnSelectWidget: !!onSelectWidget, 
            widgetId: data.id,
            target: event.target.tagName
        });
        
        if (isEditMode && onSelectWidget) {
            event.stopPropagation();
            onSelectWidget(data.id);
        }
    };

    // Bullet Points aus data extrahieren
    const bulletPoints = data.bulletPoints || [];
    
    console.log('🔍 BulletPointsWidget bullet points data:', {
        bulletPointsArray: bulletPoints,
        bulletPointsLength: bulletPoints.length,
        firstBulletPoint: bulletPoints[0]
    });

    // Image Effect Helper (wie SupremeTestimonial)
    const getImageEffect = (effect) => {
        switch (effect) {
            case 'blur':
                return { filter: 'blur(3px)' };
            case 'grayscale':
                return { filter: 'grayscale(100%)' };
            case 'sepia':
                return { filter: 'sepia(100%)' };
            case 'brightness':
                return { filter: 'brightness(1.2)' };
            case 'contrast':
                return { filter: 'contrast(1.3)' };
            case 'top-fade':
                return { 
                    filter: 'none',
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                    backgroundBlendMode: 'overlay'
                };
            default:
                return { filter: 'none' };
        }
    };

    // Layout Configuration (wie SpiritualTestimonial)
    const getLayoutConfig = () => {
        const layoutType = data.bulletLayout || 'icon-left';
        
        const layouts = {
            'icon-left': {
                bulletPointsPerRow: 2,
                iconPosition: 'left',
                textAlignment: 'left',
                iconStyle: 'horizontal'
            },
            'icon-top': {
                bulletPointsPerRow: 2,
                iconPosition: 'top',
                textAlignment: 'center',
                iconStyle: 'vertical'
            },
            'numbered': {
                bulletPointsPerRow: 1,
                iconPosition: 'left',
                textAlignment: 'left',
                iconStyle: 'numbered'
            },
            'minimal': {
                bulletPointsPerRow: 1,
                iconPosition: 'none',
                textAlignment: 'left',
                iconStyle: 'minimal'
            }
        };
        
        return layouts[layoutType] || layouts['icon-left'];
    };

    const layoutConfig = getLayoutConfig();

    // Layout Helper Functions
    const getLayoutStyle = () => {
        const bulletPointsPerRow = data.bulletPointsPerRow || layoutConfig.bulletPointsPerRow || 2;
        const bulletPointsPerRowMobile = data.bulletPointsPerRowMobile || 1;
        
        return {
            display: 'grid',
            gridTemplateColumns: isMobileLayout 
                ? `repeat(${bulletPointsPerRowMobile}, 1fr)` 
                : `repeat(${bulletPointsPerRow}, 1fr)`,
            gap: appendPx(data.bulletPointSpacing || 24),
            alignItems: 'flex-start',
            width: '100%',
            maxWidth: appendPx(data.maxWidth || 1000),
            margin: '0 auto'
        };
    };

    // Bullet Point Card Style
    const getBulletPointStyle = (index) => {
        const baseStyle = {
            display: 'flex',
            alignItems: data.bulletLayout === 'icon-top' ? 'center' : 'center', // Immer zentriert für bessere Icon-Text-Ausrichtung
            flexDirection: data.bulletLayout === 'icon-top' ? 'column' : 'row',
            gap: appendPx(data.bulletLayout === 'icon-top' ? 16 : (data.bulletLayout === 'minimal' ? 12 : 20)),
            padding: appendPx(data.bulletPointPadding || 20),
            textAlign: data.bulletLayout === 'icon-top' ? 'center' : 'left',
            height: 'fit-content',
            minHeight: appendPx(data.bulletPointMinHeight || 60) // Mindesthöhe hinzugefügt
        };

        return baseStyle;
    };

    // Icon rendering helper
    const renderBulletIcon = (bulletPoint, index) => {
        if (!bulletPoint) return null;
        
        // Numbered Layout
        if (data.bulletLayout === 'numbered') {
            return (
                <div style={{
                    width: appendPx(data.bulletIconSize || 48),
                    height: appendPx(data.bulletIconSize || 48),
                    backgroundColor: data.bulletIconBackgroundColor || 'rgba(0, 0, 0, 0.05)', // ✅ FIXED: Neutraler Fallback statt hardcoded blau
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: appendPx((data.bulletIconSize || 48) * 0.4),
                    fontWeight: '700',
                    color: data.bulletIconColor || data.titleColor || '#667eea' // ✅ IMPROVED: titleColor als Fallback
                }}>
                    {index + 1}
                </div>
            );
        }
        
        // Minimal Layout - Bullet Point Symbol
        if (data.bulletLayout === 'minimal') {
            const bulletSymbol = data.bulletSymbol || '•';
            return (
                <div style={{
                    width: appendPx(data.bulletIconSize || 24),
                    height: appendPx(data.bulletIconSize || 24),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: appendPx((data.bulletIconSize || 24) * 0.8),
                    fontWeight: '700',
                    color: data.bulletIconColor || data.titleColor || '#667eea', // ✅ IMPROVED: titleColor als Fallback
                    lineHeight: 1
                }}>
                    {bulletSymbol}
                </div>
            );
        }

        // Clean Icon Layout - Icon ohne Hintergrund
        if (data.bulletLayout === 'clean-icon') {
            const iconSrc = bulletPoint.iconType === 'upload' && bulletPoint.customIcon 
                ? bulletPoint.customIcon 
                : bulletPoint.icon;

            if (!iconSrc) {
                // Fallback: Einfacher Punkt ohne Container
                return (
                    <div style={{
                        width: appendPx(data.bulletIconSize || 32),
                        height: appendPx(data.bulletIconSize || 32),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: appendPx((data.bulletIconSize || 32) * 0.6),
                        fontWeight: '700',
                        color: data.bulletIconColor || data.titleColor || '#667eea', // ✅ IMPROVED: titleColor als Fallback
                        lineHeight: 1
                    }}>
                        •
                    </div>
                );
            }

            return (
                <div style={{
                    width: appendPx(data.bulletIconSize || 32),
                    height: appendPx(data.bulletIconSize || 32),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <img
                        src={iconSrc}
                        alt={`Bullet point ${index + 1}`}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            filter: shouldAutoInvertIcons() ? 'invert(1)' : 'none' // ✅ AUTO-INVERT: Basierend auf Container-Hintergrund
                        }}
                        onError={(e) => {
                            // Fallback wenn Bild nicht lädt
                            e.target.style.display = 'none';
                            e.target.parentNode.innerHTML = `<div style="font-size: ${(data.bulletIconSize || 32) * 0.6}px; font-weight: 700; color: ${data.bulletIconColor || '#667eea'};">•</div>`;
                        }}
                    />
                </div>
            );
        }
        
        // Icon Layout
        const iconStyle = {
            width: appendPx(data.bulletIconSize || 48),
            height: appendPx(data.bulletIconSize || 48),
            backgroundColor: data.bulletIconBackgroundColor || 'rgba(0, 0, 0, 0.05)', // ✅ FIXED: Neutraler Fallback statt hardcoded blau
            borderRadius: appendPx(data.bulletIconBorderRadius || 12),
            padding: appendPx(data.bulletIconPadding || 12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
        };

        const imageStyle = {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            filter: shouldAutoInvertIcons() ? 'invert(1)' : 'none' // ✅ AUTO-INVERT: Basierend auf Container-Hintergrund
        };

        // Icon source bestimmen
        const iconSrc = bulletPoint.iconType === 'upload' && bulletPoint.customIcon 
            ? bulletPoint.customIcon 
            : bulletPoint.icon;

        if (!iconSrc) {
            // Fallback Icon
            return (
                <div style={iconStyle}>
                    <div style={{
                        width: '16px',
                        height: '16px',
                        backgroundColor: data.bulletIconColor || data.titleColor || '#667eea', // ✅ FIXED: Verwende titleColor als Fallback statt hardcoded
                        borderRadius: '4px'
                    }} />
                </div>
            );
        }

        return (
            <div style={iconStyle}>
                <img
                    src={iconSrc}
                    alt={`Bullet point ${index + 1}`}
                    style={imageStyle}
                    onError={(e) => {
                        // Fallback wenn Bild nicht lädt
                        e.target.style.display = 'none';
                        e.target.parentNode.innerHTML = `<div style="width: 16px; height: 16px; background-color: ${data.bulletIconColor || '#667eea'}; border-radius: 4px;"></div>`;
                    }}
                />
            </div>
        );
    };

    return (
        <>
            <div 
                className="widget-container bullet-points-widget"
                style={widgetStyle}
                ref={containerRef}
                onClick={handleWidgetClick}
            >
                {/* Delete Button */}
                <div>
                    {isEditMode && (
                        <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                            <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                        </button>
                    )}
                    
                    {/* 🤖 AI REFRESH ICON */}
                    {isEditMode && (
                        <AIRefreshIcon 
                            widget={{
                                ...data,
                                type: 'BulletPointsWidget'
                            }}
                            spaceId={null}
                            onContentRefreshed={(refreshedWidget) => {
                                console.log('🤖 [BULLETPOINTSWIDGET] AI-refreshed content received:', refreshedWidget);
                                onUpdate(refreshedWidget);
                            }}
                            position="top-left"
                        />
                    )}
                    {isEditMode && showDeleteModal && (
                        <DeleteModal
                            isOpen={showDeleteModal}
                            onClose={() => setShowDeleteModal(false)}
                            onConfirm={(event) => handleDelete(event, data.id)}
                        />
                    )}
                </div>

                {/* Background Image System (wie SupremeTestimonial) */}
                {data.showBackgroundImage && data.backgroundImage && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundImage: `url(${data.backgroundImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            opacity: data.backgroundImageOpacity || 0.1,
                            ...getImageEffect(data.imageEffect || 'none'),
                            transition: 'opacity 0.4s ease-in-out'
                        }} />
                    </div>
                )}

                {/* Content Container */}
                <div style={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: appendPx(data.contentSpacing || 40),
                    alignItems: 'center', // ✅ FIXED: Titel immer mittig ausrichten
                    textAlign: 'center', // ✅ FIXED: Titel immer mittig ausrichten
                    maxWidth: appendPx(data.maxWidth || 1000),
                    margin: '0 auto'
                }}>

                    {/* Title - Inline Editable with Show/Hide Toggle */}
                    {data.showTitleText !== false && data.titleText && (
                        <h2 
                            style={{
                                // Title Kombinierte Effects
                                ...((!data.titleEffect || data.titleEffect === 'none') && {
                                    color: data.titleColor || '#000000'
                                }),
                                ...getTextEffectStyles(data.titleEffect),
                                
                                fontSize: appendPx(data.titleSize || 36),
                                fontWeight: data.titleWeight || '700',
                                fontFamily: data.titleFont || fonts.globalBigFont,
                                margin: '0',
                                lineHeight: '1.3',
                                cursor: isEditMode ? 'text' : 'default',
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 36),
                                '--normal-size': appendPx(data.titleSize || 36),
                                '--big-size': appendPx(data.titleSizeBig || data.titleSize || 36)
                            }}
                            onClick={isEditMode ? () => {
                                // Simple inline editing for title
                                const newTitle = prompt('Edit Title:', data.titleText);
                                if (newTitle !== null && onUpdate) {
                                    onUpdate({ ...data, titleText: newTitle });
                                }
                            } : undefined}
                            title={isEditMode ? "Click to edit title" : undefined}
                        >
                            {data.titleText}
                        </h2>
                    )}

                    {/* Bullet Points Grid */}
                    {bulletPoints.length > 0 && (
                        <div style={{
                            ...getLayoutStyle(),
                            width: '100%',
                            // ✅ FIXED: Bullet Points behalten ihre eigene Ausrichtung (nicht vom Container übernommen)
                            textAlign: data.bulletPointAlignment === 'center' ? 'center' : 'left'
                        }}>
                            {bulletPoints.map((bulletPoint, index) => {
                                const bulletStyle = getBulletPointStyle(index);

                                return (
                                    <div
                                        key={bulletPoint.id || index}
                                        style={{
                                            ...bulletStyle,
                                            '--index': index
                                        }}
                                        className={`bullet-point-card ${data.bulletLayout || 'icon-left'}`}
                                    >
                                        {/* Bullet Icon */}
                                        {renderBulletIcon(bulletPoint, index)}

                                        {/* Bullet Content */}
                                        <div style={{
                                            flex: 1,
                                            minWidth: 0
                                        }}>
                                            {/* Rich Text Content */}
                                            <div 
                                                style={{
                                                    fontSize: appendPx(data.bulletPointSize || 16),
                                                    fontWeight: data.bulletPointWeight || '400',
                                                    fontFamily: data.bulletPointFont || fonts.globalSmallFont,
                                                    color: data.bulletPointColor || data.bulletTextColor || data.titleColor || '#333333', // ✅ IMPROVED: Mehrere Fallbacks für bessere Space-DB Integration
                                                    lineHeight: data.bulletPointLineHeight || 1.6,
                                                    margin: '0'
                                                }}
                                                dangerouslySetInnerHTML={{ 
                                                    __html: bulletPoint.richText || 'Enter bullet point text...' 
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Fallback wenn keine Bullet Points */}
                    {bulletPoints.length === 0 && (
                        <div style={{
                            padding: '40px',
                            border: isEditMode ? '2px dashed #dee2e6' : '1px solid #e9ecef',
                            borderRadius: '12px',
                            backgroundColor: '#f8f9fa',
                            color: data.bulletTextColor || data.titleColor || '#6c757d', // ✅ IMPROVED: Space-DB Farbe statt hardcoded
                            textAlign: 'center'
                        }}>
                            <p style={{ margin: 0, fontSize: '16px' }}>
                                {isEditMode ? 'Add bullet points via the Edit Panel' : 'No bullet points available'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default BulletPointsWidget;
